import { TElement } from '@udecode/plate';
import axios from 'axios';
import config from 'config';
import { API } from 'src/api';
import useSWR, { ConfigInterface } from 'swr';

import { DocType } from '../types/doc';

const useDocById = ({ id }: { id: string; configs?: ConfigInterface }) => {
  const { data, error, revalidate, ...others } = useSWR(`/creation/get/${id}`, API.get);
  const _data = data?.data?.data ? formatDocData(data?.data?.data) : null;
  return {
    data: _data as DocType | null,
    error,
    isLoading: !data && !error,
    revalidate,
    ...others,
  };
};

export const getDoc = async ({ docId, isPublic }: { docId: string; isPublic?: boolean }) => {
  try {
    const response = await API.get(
      `${isPublic ? `/creation/public?id=${docId}` : `/creation/get/${docId}`}`
    );

    if (response.data.success) {
      return formatDocData(response.data.data);
    }
  } catch (error) {
    console.error(error);
  }
  return null;
};

export const getDocument = async ({ docId }: { docId: string }) => {
  try {
    const response = await API.get(`/creation?id=${docId}`);

    if (response.data.success) {
      return formatDocData(response.data.data);
    }
  } catch (error) {
    console.error(error);
  }
  return null;
};

const inviteUsers = async ({
  docId,
  access,
  memberIds,
  emails,
}: {
  docId: string;
  access: string;
  memberIds: string;
  emails: string;
}) => {
  const payload = {
    access: access,
    add: {
      memberIds: memberIds,
      emails: emails,
    },
  };
  try {
    const response = await API.put(`/creation?id=${docId}`, payload);
    if (response.data.success) return true;
    else return false;
  } catch (error) {
    console.error(error);
    return false;
  }
};

const changeAccess = async ({
  memberId,
  access,
  docId,
}: {
  memberId: string;
  access: string;
  docId: string;
}) => {
  if (access === 'remove') {
    await API.put(`/creation?id=${docId}`, { remove: memberId });
  } else {
    const payload = {
      access: access,
      edit: [memberId],
    };
    await API.put(`/creation?id=${docId}`, payload);
  }
};

const changeLegalToDoc = async ({
  doc,
  lessonSlug,
  docId,
}: {
  doc: TElement;
  lessonSlug: string;
  docId: string;
}) => {
  try {
    const response = await API.post('creation/legal/create', {
      document: doc,
      lesson: lessonSlug,
      creationId: docId,
    });
    if (response.data.success) return true;
    else return false;
  } catch (error) {
    console.error(error);
    return false;
  }
};

async function downloadPDF({ docId, title }: { docId: string; title: string }) {
  const lAPI = axios.create({ baseURL: config.apiBase });
  lAPI.defaults.headers['Content-Type'] = 'application/pdf';
  lAPI.defaults.headers.Accept = 'application/pdf';
  lAPI.defaults.responseType = 'blob';
  try {
    const data = await lAPI.get(`/creation/export?creationId=${docId}`);
    const element = document.createElement('a');
    const file = new Blob([data.data]);
    element.href = URL.createObjectURL(file);
    element.download = `${title}.pdf` ?? 'document.pdf';
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

const changeTaskProgress = async ({
  total,
  completed,
  docId,
}: {
  total: number;
  completed: number;
  docId: string;
}): Promise<{
  pointsAchieved: number;
  alreadyCompleted: boolean;
  completed: boolean;
} | null> => {
  const payload = { total, completed, creationId: docId };
  try {
    const response = await API.put('/creation/task/progress', payload);
    if (response.data.success) {
      return response.data.data as {
        pointsAchieved: number;
        alreadyCompleted: boolean;
        completed: boolean;
      };
    }
    return null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const assignTask = async ({
  creationId,
  taskId,
  taskName,
  text,
  dateRange,
  members,
  completed,
}: {
  creationId: string;
  taskId: string;
  taskName?: TElement[];
  text?: string;
  dateRange?: [string, string];
  members?: string[];
  completed?: boolean;
}) => {
  try {
    const response = await API.post('/creation/task/assign', {
      creationId,
      taskId,
      taskName,
      text,
      dateRange,
      members,
      completed,
    });
    if (response.data.success) {
      return true;
    }
  } catch (error) {
    console.error(error);
  }
  return false;
};

const removeTask = async ({ taskId }: { taskId: string }) => {
  try {
    const response = await API.delete(`/creation/task/assign?taskId=${taskId}`);
    if (response.data.success) {
      return true;
    }
  } catch (error) {
    console.error(error);
  }
  return false;
};

const useDocServices = () => {
  return {
    useDocById,
    inviteUsers,
    changeAccess,
    changeLegalToDoc,
    downloadPDF,
    changeTaskProgress,
    assignTask,
    removeTask,
    getDoc,
    getDocument,
  };
};

export default useDocServices;

export function formatDocData(creation): DocType {
  return {
    ...creation,
    title: creation?.title,
    tag: creation?.tag,
    modified: {
      author: creation?.owner?.firstname + ' ' + creation?.owner?.lastname,
      timing: creation?.updatedAt,
    },
    tasks: { completed: creation?.status?.progress, total: creation?.status?.total },
    image: creation?.image,
    lessonSlug: creation?.lesson,
    creationId: creation?.creationId,
    document: creation?.document,
    type: creation?.type,
    questions: creation?.questions,
  };
}
